import { createColumnHelper } from '@tanstack/react-table';
import { clipTextWithEllipses } from './textUtils';
import './util-styles/text-styles.css';
import { useState, useRef, useEffect } from 'react';
import { CandidateService } from '../services/candidateService';
import SigniteButton from '../components/ui-elements/signite-button/signiteButton';
import threeDotsImage from '../../src/assets/images/Ic_more.png';
import { decodeJWT } from '../services/candidateService';

export const prepareCandidateData = (candidateData, candidateLink = null, withStatusAction = false, withLastLoggedIn = false, withActionColumn = false, isCuratedScreen = false, onCurateClick) => {
  const data = candidateData?.candidates || [];
  const columnHelper = createColumnHelper();
  const decoded = decodeJWT(window.localStorage.getItem('authToken'));
  const currentUserRole = decoded?.roles;
  const isDataEntryExe = currentUserRole === "DATA_ENTRY_EXE";
  const columns = [
    columnHelper.accessor((row) => row.candidateId, {
      id: "candidateId",
      cell: (info) => {
        const row = info.row.original;
        const candidateId = info.getValue();

        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <a
              href={
                candidateLink
                  ? `${candidateLink}${candidateId}`
                  : `/candidate/candidate-personal-info?candidateId=${candidateId}`
              }
            >
              {candidateId}
            </a>

            {isCuratedScreen && row.folderName && (
              <span style={{ fontSize: "12px", color: "#555", marginTop: "2px" }}>
                ({row.folderName})
              </span>
            )}
          </div>
        );
      },
      header: () => <span>Candidate ID</span>,
    }),

    columnHelper.accessor('candidate.personalDetails.firstName', {
      id: 'candidate.personalDetails.firstName',
      header: () => 'First Name',
      cell: (info) => (
        <div className="truncate" style={{ maxWidth: 220 }}>{info.getValue()}</div>
      ),
    }),
    columnHelper.accessor('candidate.personalDetails.lastName', {
      header: () => 'Last Name',
      cell: (info) => (
        <div className="truncate" style={{ maxWidth: 220 }}>{info.getValue()}</div>
      ),
    }),
    columnHelper.accessor('candidate.workExperience.currentRole.designation', {
      header: () => 'Designation',
      cell: (info) => clipTextWithEllipses(info.renderValue(), 40),
    }),
    columnHelper.accessor(row => {
      const assigned = row.assignedTo;
      if (!assigned) return '';
      return assigned.firstName + ' ' + (assigned.lastName || '');
    }, {
      id: 'assignedTo',
      header: () => 'Assigned To',
      cell: (info) => (
        <div className="truncate" style={{ maxWidth: 220 }}>
          {info.getValue()}
        </div>
      ),
    }),

    columnHelper.accessor('status', {
      header: () => 'Candidate Profile Status',
      cell: (info) => {
        const value = info.getValue();
        const color = value === 'VALIDATED' ? 'green' : 'red';
        return (
          <div className="truncate" style={{ maxWidth: 220, color }}>
            {value}
          </div>
        );
      },
    }),

    columnHelper.accessor('searchStatus', {
      header: () => 'Candidate Search Status',
      cell: (info) => {
        const value = info.getValue();
        const color = value === 'ACTIVE' ? 'green' : 'red';
        return (
          <div className="truncate" style={{ maxWidth: 220, color }}>
            {value}
          </div>
        );
      },
    }),
  ];

  if (onCurateClick && !isDataEntryExe) {
    columns.push(
      columnHelper.accessor(row => row.candidateId, {
        id: "curate",
        cell: (info) => {
          const candidateId = info.getValue();
          return (
            <SigniteButton
              variant="primary"
              style={{ padding: "4px 10px", fontSize: "12px", height: 36, width: 120 }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onCurateClick(candidateId);
              }}
            >
              Curate
            </SigniteButton>
          );
        },
        header: () => (
          <div className="header-cell">
            <span>Curate</span>
          </div>
        ),
      })
    );
  }

  if (withLastLoggedIn) {
    columns.splice(3, 0, columnHelper.accessor('lastLoggedInAt', {
      header: () => 'Last Logged In',
      cell: (info) => {
        const val = info.getValue();
        return val
          ? new Date(val).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          })
          : '-';
      },
    }));
  }

  if (isCuratedScreen) {
    let openMenuCandidateIdState = { value: null };
    let setOpenMenuCandidateIdState = (val) => {
      openMenuCandidateIdState.value = val;
    };

    columns.push(
      columnHelper.display({
        id: "curatedAction",
        header: "Action",
        cell: ({ row }) => {
          const candidateId = row.original.candidateId;
          const folderId = row.original.folderId;
          const fullName = row.original.fullName;

          const [menuOpen, setMenuOpen] = useState(false);
          const menuRef = useRef(null);

          const handleMenuToggle = (e) => {
            e.stopPropagation();

            if (openMenuCandidateIdState.value !== candidateId) {
              setOpenMenuCandidateIdState(candidateId);
            }

            setMenuOpen((prev) => !prev);
          };

          useEffect(() => {
            const handleClick = (event) => {
              if (
                menuRef.current &&
                !menuRef.current.contains(event.target)
              ) {
                setMenuOpen(false);
              }
            };
            document.addEventListener("mousedown", handleClick);
            return () => document.removeEventListener("mousedown", handleClick);
          }, []);

          return (
            <div className="curated-action-cell" ref={menuRef}>
              <img
                src={threeDotsImage}
                alt="Actions"
                className="curated-three-dots-icon"
                onClick={handleMenuToggle}
              />

              {menuOpen && (
                <div className="curated-menu">

                  <div
                    className="curated-menu-item danger"
                    onClick={() => {
                      if (typeof window !== "undefined" && window.onUncurateCandidateClick) {
                        window.onUncurateCandidateClick(candidateId, folderId);
                      }
                      setMenuOpen(false);
                    }}
                  >
                    <span>Uncurate Candidate</span>
                  </div>
                </div>
              )}

            </div>
          );
        },
      })
    );
  }


  if (withStatusAction) {
    columns.push(
      columnHelper.display({
        id: 'status',
        header: 'Action',
        cell: ({ row }) => {
          const candidateId = row.original.candidateId;
          const [approved, setApproved] = useState(false);
          const [loading, setLoading] = useState(false);

          const handleApprove = async () => {
            try {
              setLoading(true);
              await CandidateService.approveCandidateRequest(candidateId);
              setApproved(true);

              if (typeof window !== "undefined" && window.onCandidateApproved) {
                window.onCandidateApproved();
              }


            } catch (err) {
              console.error("Approval failed:", err);
            } finally {
              setLoading(false);
            }
          };

          if (approved) {
            return <span style={{ backgroundColor: '#F35824', color: '#FFFFFF', border: 'none', borderRadius: '100px', height: '35px', width: '90px' }} >Approved</span>;
          }

          return (
            <button
              style={{ backgroundColor: '#F35824', color: '#FFFFFF', border: 'none', borderRadius: '100px', height: '35px', width: '90px' }}
              onClick={handleApprove}
              disabled={loading}
            >
              {loading ? 'Approving...' : 'Approve'}
            </button>
          );
        },
      })
    );
  }

  return { columns, data };
};
